import { useState } from 'react';

// Format-only field validation hook. See Setup Guide validation philosophy:
// empty fields never block save, only malformed user input does. Completion
// is the resolver's job, not the form's.

export type FieldErrors<T extends string> = Partial<Record<T, string>>;
export type TouchedFields<T extends string> = Partial<Record<T, boolean>>;

export interface UseFieldErrorsResult<T extends string> {
  errors: FieldErrors<T>;
  touched: TouchedFields<T>;
  canSave: boolean;
  showError: (field: T) => string | null;
  handleBlur: (field: T) => void;
  markAllTouched: () => void;
  reset: () => void;
}

export function useFieldErrors<T extends string>(
  validate: () => FieldErrors<T>,
): UseFieldErrorsResult<T> {
  const [touched, setTouched] = useState<TouchedFields<T>>({});

  const errors = validate();
  const canSave = Object.values(errors).every(v => !v);

  const showError = (field: T): string | null => {
    if (!touched[field]) return null;
    return errors[field] ?? null;
  };

  const handleBlur = (field: T) => {
    setTouched(prev => (prev[field] ? prev : { ...prev, [field]: true }));
  };

  const markAllTouched = () => {
    const all: TouchedFields<T> = {};
    for (const k of Object.keys(errors) as T[]) all[k] = true;
    setTouched(prev => ({ ...prev, ...all }));
  };

  const reset = () => setTouched({});

  return { errors, touched, canSave, showError, handleBlur, markAllTouched, reset };
}
